import React, { useState } from 'react'
import { ClientQuestion } from '@mission.io/question-toolkit'
import {
	generateTitle,
	isClozeDropdown,
	isImageLabeling,
	Image,
} from '../../utils'
import { TemplateReadOnly } from '../../stories/cloze-dropdown/Teacher'
import { StudentResponse } from '../../model/general'
import { MdClose } from 'react-icons/md'
import 'styled-components/macro'
import { styled } from 'styled-components'
import ImageWrapper from '../image-wrapper/ImageWrapper'
import OptionalMarkdown from '../Markdown/OptionalMarkdown'

export type AnalyticsQuestionProps = {
	studentResponse?: StudentResponse | null
	onRequestClose: () => void
	studentName?: string | undefined
	question: ClientQuestion
	title: string
	children: JSX.Element
}

/**
 * It is the single student single question modal.  It takes in a specific
 * question and student, and shows if the student got that question right, wrong, or unmarked (meaning its an unmarked FR question).
 * @param {AnalyticsQuestionProps} props
 * @returns {JSX.Element}
 */

export default function AnalyticsSingleStudentContainer(
	props: AnalyticsQuestionProps
): JSX.Element {
	const {
		question,
		onRequestClose,
		studentResponse,
		studentName = 'Unknown Student',
		title,
		children,
	} = props

	const questionPhrase = isClozeDropdown(question) ? (
		<TemplateReadOnly template={question.template} />
	) : !isImageLabeling(question) ? (
		<OptionalMarkdown>{question.phrase}</OptionalMarkdown>
	) : (
		''
	)

	const [hideQuestionImage, setHideQuestionImage] = useState(true)

	const showQuestionImage = !isImageLabeling(question)

	return (
		<div className="rounded-lg overflow-hidden">
			<div className="flex justify-between items-center bg-brand-deep-blue p-4 text-base font-semibold">
				{title} - <span className="notranslate">{studentName}</span>
				<ExitX onClick={onRequestClose} />
			</div>
			<div className="flex flex-col p-4 bg-brand-cloud-blue text-brand-space-blue text-start">
				<div className="flex justify-between font-semibold">
					<span className="font-semibold">
						{generateTitle(question, studentResponse)}
					</span>
					{isImageLabeling(question) ? (
						<button
							onClick={() => setHideQuestionImage(!hideQuestionImage)}
							className="hover:underline text-brand-horizon-blue">
							{hideQuestionImage ? 'See Image' : 'Hide Image'}
						</button>
					) : (
						''
					)}
				</div>
				{questionPhrase && (
					<div className="text-base font-normal mb-2">{questionPhrase}</div>
				)}
				{isImageLabeling(question) && !hideQuestionImage ? (
					<ImageWrapper question={question} isReadonly />
				) : (
					showQuestionImage && <Image media={question.media} />
				)}
				{children}
			</div>
		</div>
	)
}

export const ExitX = styled(MdClose)`
	cursor: pointer;
	font-size: 1.4em;
	text-align: center;
	color: ${({ theme }) => theme.neutralDarker};

	&:hover {
		transform: scale(1.25);
	}
`
